import {
  MutateVariablePayload,
  VariableDataResult,
  VariableDto,
  VariableSourceSettingsDto,
} from '@thingslog/repositories';
import {
  Autocomplete,
  AutocompleteRenderGetTagProps,
  AutocompleteRenderInputParams,
  Button,
  Chip,
  TextField,
} from '@mui/material';
import { QueryKeys } from '@thingslog/queries';
import { useQuery } from '@tanstack/react-query';
import React, { FC, useEffect, useMemo } from 'react';
import { Controller, FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { VariableSourceSettingsFactory } from '../../../factories/VariableSourceSettings.factory';

interface Props {
  variable: VariableDto;
  variableSources: VariableSourceSettingsDto[];
  companyId?: number | null;
  disabled?: boolean;
  onSubmit: (id: string, payload: MutateVariablePayload) => void;
  onCancel: () => void;
}

interface VariableValueFormValues {
  value: string | string[];
}

interface VariableSourceOption {
  name: string;
  value: string;
}

export const VariableValueForm: FC<Props> = ({
  variable,
  variableSources,
  companyId,
  disabled,
  onSubmit,
  onCancel,
}: Props) => {
  const { t } = useTranslation();
  const defaultValues = useMemo<VariableValueFormValues>(() => {
    return {
      value: variable.value,
    };
  }, [variable.value]);

  const { control, handleSubmit, reset } = useForm<VariableValueFormValues>({
    mode: 'onChange',
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const selectedVariableSource = useMemo(() => {
    if (!variable.variableSource) {
      return null;
    }

    return (
      variableSources.find(
        (source: VariableSourceSettingsDto) => source.id === variable.variableSource?.id
      ) ?? variable.variableSource
    );
  }, [variable.variableSource, variableSources]);

  const sourceOptionsQuery = useQuery(
    [QueryKeys.GetVariableSources, 'options', selectedVariableSource?.id, companyId],
    async (): Promise<VariableSourceOption[]> => {
      if (!selectedVariableSource) {
        return [];
      }

      const result: VariableDataResult | null = await VariableSourceSettingsFactory.execute(
        selectedVariableSource.source.type,
        companyId ?? null
      );

      return result?.result ?? [];
    },
    {
      enabled: !!selectedVariableSource,
      refetchOnWindowFocus: false,
    }
  );

  const sourceOptions = sourceOptionsQuery.data ?? [];
  const isMultiValue = variable.type === 'multi-value';

  const handleFormSubmit = (values: VariableValueFormValues): void => {
    if (variable.type === 'single-value') {
      onSubmit(variable.id, {
        type: 'single-value',
        name: variable.name,
        label: variable.label,
        variableSourceId: variable.variableSource?.id || null,
        value: Array.isArray(values.value) ? values.value[0] ?? '' : values.value,
      });
      return;
    }

    const value = Array.isArray(values.value)
      ? values.value
      : values.value
          .split(',')
          .map((item: string) => item.trim())
          .filter((item: string) => item.length > 0);

    onSubmit(variable.id, {
      type: 'multi-value',
      name: variable.name,
      label: variable.label,
      variableSourceId: variable.variableSource?.id || null,
      value,
    });
  };

  return (
    <form className="flex flex-col gap-4 p-4" onSubmit={handleSubmit(handleFormSubmit)}>
      <div data-dashboard-tour="variable-form-value">
        <Controller
          name="value"
          control={control}
          rules={{
            validate: (value: string | string[]): string | boolean => {
              if (Array.isArray(value)) {
                return (
                  value.length > 0 ||
                  t(
                    'dashboard_variable_at_least_one_value_required',
                    'At least one value is required'
                  )
                );
              }

              if (isMultiValue) {
                const values = value
                  .split(',')
                  .map((item: string) => item.trim())
                  .filter((item: string) => item.length > 0);

                return (
                  values.length > 0 ||
                  t(
                    'dashboard_variable_at_least_one_value_required',
                    'At least one value is required'
                  )
                );
              }

              return (
                value.trim().length > 0 ||
                t('dashboard_variable_value_required', 'Value is required')
              );
            },
          }}
          render={({ field, fieldState }: FieldValues): JSX.Element => {
            if (selectedVariableSource && isMultiValue) {
              const selectedOptions = sourceOptions.filter((option: VariableSourceOption) =>
                Array.isArray(field.value) ? field.value.includes(option.value) : false
              );

              return (
                <Autocomplete
                  multiple
                  size="small"
                  options={sourceOptions}
                  value={selectedOptions}
                  loading={sourceOptionsQuery.isLoading}
                  disabled={disabled}
                  getOptionLabel={(option: VariableSourceOption): string => option.name}
                  isOptionEqualToValue={(
                    option: VariableSourceOption,
                    value: VariableSourceOption
                  ): boolean => option.value === value.value}
                  onChange={(_: React.SyntheticEvent, options: VariableSourceOption[]): void => {
                    field.onChange(options.map((option: VariableSourceOption) => option.value));
                  }}
                  renderTags={(
                    value: VariableSourceOption[],
                    getTagProps: AutocompleteRenderGetTagProps
                  ): JSX.Element[] =>
                    value.map((option: VariableSourceOption, index: number) => {
                      const { key, ...tagProps } = getTagProps({ index });

                      return <Chip {...tagProps} key={key} label={option.name} size="small" />;
                    })
                  }
                  renderInput={(params: AutocompleteRenderInputParams): JSX.Element => (
                    <TextField
                      {...params}
                      label={t('dashboard_variable_value', 'Value')}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              );
            }

            if (selectedVariableSource) {
              const selectedOption =
                sourceOptions.find(
                  (option: VariableSourceOption) => option.value === field.value
                ) ?? null;

              return (
                <Autocomplete
                  size="small"
                  options={sourceOptions}
                  value={selectedOption}
                  loading={sourceOptionsQuery.isLoading}
                  disabled={disabled}
                  getOptionLabel={(option: VariableSourceOption): string => option.name}
                  isOptionEqualToValue={(
                    option: VariableSourceOption,
                    value: VariableSourceOption
                  ): boolean => option.value === value.value}
                  onChange={(
                    _: React.SyntheticEvent,
                    option: VariableSourceOption | null
                  ): void => {
                    field.onChange(option?.value ?? '');
                  }}
                  renderInput={(params: AutocompleteRenderInputParams): JSX.Element => (
                    <TextField
                      {...params}
                      label={t('dashboard_variable_value', 'Value')}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              );
            }

            return (
              <TextField
                value={Array.isArray(field.value) ? field.value.join(', ') : field.value}
                onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                  field.onChange(event.target.value);
                }}
                label={
                  isMultiValue
                    ? t('dashboard_variable_values', 'Values')
                    : t('dashboard_variable_value', 'Value')
                }
                size="small"
                fullWidth
                disabled={disabled}
                error={!!fieldState.error}
                helperText={
                  fieldState.error?.message ||
                  (isMultiValue
                    ? t('dashboard_variable_values_helper', 'Separate multiple values with commas.')
                    : '')
                }
              />
            );
          }}
        />
      </div>

      <div className="flex justify-end gap-2" data-dashboard-tour="variable-form-actions">
        <Button variant="outlined" onClick={onCancel} disabled={disabled}>
          {t('cancel', 'Cancel')}
        </Button>
        <Button type="submit" variant="contained" disabled={disabled}>
          {t('dashboard_apply_locally', 'Apply locally')}
        </Button>
      </div>
    </form>
  );
};
